import React from 'react';
import { ArrowRight, TrendingUp, Target, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import Reveal from '../components/Reveal';

const CaseStudies = () => {
  const { t } = useTranslation();
  
  const caseStudies = [
    {
      id: 'pachakuti',
      category: t('caseStudies.pachakuti.category'),
      client: t('caseStudies.pachakuti.client'),
      industry: t('caseStudies.pachakuti.industry'),
      challenge: t('caseStudies.pachakuti.challenge'),
      solution: t('caseStudies.pachakuti.solution'),
      results: [
        { metric: t('caseStudies.metrics.revenue'), value: '£50k', period: '60 days' },
        { metric: t('caseStudies.metrics.categoryRanking'), value: 'Top 4', period: 'Cacao' },
        { metric: t('caseStudies.metrics.ctrIncrease'), value: '5x', period: 'post-optimization' },
        { metric: t('caseStudies.metrics.troas'), value: '13.00', period: '2.5 months' }
      ],
      image: 'https://customer-assets.emergentagent.com/job_design-75/artifacts/mt1i9td5_Pachakuti.jpg',
      pressLink: 'https://www.confectioneryproduction.com/news/57201/ecuadorian-and-uk-cacao-firm-pachakuti-reports-online-success/',
      testimonial: t('caseStudies.pachakuti.testimonial'),
      author: t('caseStudies.pachakuti.author')
    },
    {
      id: 'hair-guru',
      category: t('caseStudies.hairGuru.category'),
      client: t('caseStudies.hairGuru.client'),
      industry: t('caseStudies.hairGuru.industry'),
      challenge: t('caseStudies.hairGuru.challenge'),
      solution: t('caseStudies.hairGuru.solution'),
      results: [
        { metric: t('caseStudies.metrics.revenue'), value: '£25k', period: '30 days' },
        { metric: t('caseStudies.metrics.ctrImprovement'), value: '5x', period: 'immediate' },
        { metric: t('caseStudies.metrics.categoryRanking'), value: 'Top 4', period: 'haircare' }
      ],
      image: 'https://customer-assets.emergentagent.com/job_design-75/artifacts/orz3hp50_Jamie%20Shepard%20hair%20Guru.jpg',
      pressLink: null,
      testimonial: t('caseStudies.hairGuru.testimonial'),
      author: t('caseStudies.hairGuru.author')
    },
    {
      id: 'leon',
      category: t('caseStudies.leon.category'),
      client: t('caseStudies.leon.client'),
      industry: t('caseStudies.leon.industry'),
      challenge: t('caseStudies.leon.challenge'),
      solution: t('caseStudies.leon.solution'),
      results: [
        { metric: t('caseStudies.metrics.freshTrial'), value: 'Sold Out', period: '3 weeks' },
        { metric: t('caseStudies.metrics.plannedDuration'), value: '12 weeks', period: 'exceeded' },
        { metric: t('caseStudies.metrics.nextPhase'), value: 'New Products', period: 'ready meals' }
      ],
      image: 'https://customer-assets.emergentagent.com/job_design-75/artifacts/vdymt266_LEON.jpg',
      pressLink: null,
      testimonial: t('caseStudies.leon.testimonial'),
      author: t('caseStudies.leon.author')
    },
    {
      id: 'reborn',
      category: t('caseStudies.reborn.category'),
      client: t('caseStudies.reborn.client'),
      industry: t('caseStudies.reborn.industry'),
      challenge: t('caseStudies.reborn.challenge'),
      solution: t('caseStudies.reborn.solution'),
      results: [
        { metric: t('caseStudies.metrics.buyBoxShare'), value: '99%', period: 'from 65%' },
        { metric: t('caseStudies.metrics.fbaFbm'), value: 'Optimized', period: 'by SKU' },
        { metric: t('caseStudies.metrics.usMarket'), value: 'Ready', period: 'roadmap built' },
        { metric: t('caseStudies.metrics.sustainability'), value: 'Certified', period: 'Climate Pledge' }
      ],
      image: 'https://customer-assets.emergentagent.com/job_design-75/artifacts/i714ugcm_Reborn%20Team.jpeg',
      pressLink: 'https://www.housewaresnews.net/amazon-selects-reborn-for-its-sustainability-accelerator/',
      testimonial: t('caseStudies.reborn.testimonial'),
      author: t('caseStudies.reborn.author')
    }
  ];

  return (
    <>
      <SEO 
        title="Amazon Marketing Case Studies | Proven Client Results & Success Stories"
        description="Real Amazon PPC & SEO case studies showing 3-5x ROI, 240% sales growth, and international expansion success. See how we grow e-commerce brands."
        keywords="amazon case study, amazon ppc results, amazon seo case study, ecommerce success stories, amazon agency results 2026"
        canonical="https://www.superfly-commerce.com/case-studies"
      />
      <div className="min-h-screen bg-gray-50 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - Mobile Optimized */}
        <Reveal className="text-center mb-6">
          <Badge className="bg-green-100 text-[#22C55E] mb-4 text-xs sm:text-sm">{t('caseStudies.badge')}</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-2">
            {t('caseStudies.title')} <span className="text-[#22C55E]">{t('caseStudies.titleGreen')}</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-2">
            {t('caseStudies.subtitle')}
          </p>
        </Reveal>

        {/* Case Studies - Mobile Optimized */}
        <div className="space-y-12 sm:space-y-16 md:space-y-20">
          {caseStudies.map((study, idx) => (
            <Reveal key={study.id} y={40}>
              <div className={`flex flex-col ${
              idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
            } gap-6 sm:gap-8 md:gap-12 items-center`}>
              {/* Image - Mobile Optimized */}
              <div className="w-full lg:w-1/2">
                {study.pressLink ? (
                  <a 
                    href={study.pressLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl hover:shadow-3xl transition-shadow duration-300"
                  >
                    <img 
                      src={study.image} 
                      alt={study.client}
                      className="w-full h-64 sm:h-80 md:h-96 object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </a>
                ) : (
                  <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl">
                    <img 
                      src={study.image} 
                      alt={study.client}
                      className="w-full h-64 sm:h-80 md:h-96 object-cover"
                    />
                  </div>
                )}
              </div>

              {/* Content - Mobile Optimized */}
              <div className="w-full lg:w-1/2 px-2">
                <Badge className="bg-blue-100 text-blue-600 mb-3 sm:mb-4 text-xs sm:text-sm">{study.category}</Badge>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">{study.client}</h2>
                <p className="text-base sm:text-lg text-gray-600 mb-2">{study.industry}</p>
                
                <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                      <Target className="w-4 h-4 sm:w-5 sm:h-5 text-[#22C55E]" />
                      {t('caseStudies.challenge')}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700">{study.challenge}</p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                      <Award className="w-4 h-4 sm:w-5 sm:h-5 text-[#22C55E]" />
                      {t('caseStudies.solution')}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-700">{study.solution}</p>
                  </div>
                </div>

                {/* Results - Mobile Optimized */}
                <div className={`mt-6 sm:mt-8 grid ${study.results.length === 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'} gap-3 sm:gap-4`}>
                  {study.results.map((result, ridx) => (
                    <div key={ridx} className="bg-green-50 p-3 sm:p-4 rounded-lg sm:rounded-xl text-center">
                      <div className="text-2xl sm:text-3xl font-bold text-[#22C55E] mb-1">{result.value}</div>
                      <div className="text-xs sm:text-sm text-gray-600 mb-1">{result.metric}</div>
                      <div className="text-xs text-gray-500">{result.period}</div>
                    </div>
                  ))}
                </div>

                {/* Testimonial */}
                <div className="mt-8 bg-white p-6 rounded-xl border-l-4 border-[#22C55E]">
                  <p className="text-gray-700 italic mb-3">&ldquo;{study.testimonial}&rdquo;</p>
                  <p className="text-sm font-semibold text-gray-900">{study.author}</p>
                </div>

                <a href="/#contact">
                  <Button className="mt-6 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-full">
                    {t('caseStudies.getResults')} <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* CTA */}
        <Reveal className="mt-20 bg-gradient-to-r from-[#22C55E] to-[#16A34A] rounded-3xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-4">{t('caseStudies.ctaTitle')}</h2>
          <p className="text-xl mb-8 opacity-90">
            {t('caseStudies.ctaSubtitle')}
          </p>
          <a href="/#contact">
            <Button className="bg-white text-[#22C55E] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold">
              {t('caseStudies.ctaButton')} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </Reveal>
      </div>
    </div>
    </>
  );
};

export default CaseStudies;